/**
 * Insert Shop Pay Installments when this row nears the viewport or after
 * window load, whichever comes first. Markup stays in a <template> so
 * Shopify does not upgrade <shopify-payment-terms> on the hero path.
 */
(function () {
  const ROOT_MARGIN = '200px 0px';

  function liveTerms(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    return Array.from(root.querySelectorAll('shopify-payment-terms')).filter(function (el) {
      return !el.closest('template');
    });
  }

  function syncVariant(scope, variantId) {
    if (!scope || !variantId) return;
    const id = String(variantId);
    const input = scope.querySelector('input[name="id"]');
    if (input && input.value !== id) input.value = id;
    const terms = scope.querySelector('shopify-payment-terms');
    if (terms && terms.getAttribute('variant-id') !== id) {
      terms.setAttribute('variant-id', id);
    }
  }

  function incomingFromHtml(html) {
    if (!html || typeof html.querySelector !== 'function') return { form: null, terms: null };
    const host = html.querySelector('deferred-payment-terms');
    const template = host && host.querySelector('template');
    const searchRoot = (template && template.content) || host || html;
    return {
      form: searchRoot.querySelector('form.installment, form[id^="product-form-installment"]'),
      terms: searchRoot.querySelector('shopify-payment-terms'),
    };
  }

  class DeferredPaymentTerms extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.onReveal = this.reveal.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      if (this.dataset.inserted === 'true' || liveTerms(this).length) {
        this.dataset.inserted = 'true';
        return;
      }

      if (document.readyState === 'complete') {
        this.reveal();
        return;
      }

      window.addEventListener('load', this.onReveal, { once: true });
      if (typeof IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver(
          function (entries) {
            if (entries.some(function (entry) { return entry.isIntersecting; })) this.reveal();
          }.bind(this),
          { root: null, rootMargin: ROOT_MARGIN, threshold: 0 }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.teardown();
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      this.dataset.bound = 'false';
    }

    teardown() {
      if (this.onReveal) window.removeEventListener('load', this.onReveal);
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    currentVariantId() {
      const productInfo = this.closest('product-info');
      const sectionId = (productInfo && (productInfo.dataset.section || productInfo.dataset.originalSection)) || this.dataset.sectionId;
      if (!sectionId) return '';
      const form = document.getElementById('product-form-' + sectionId);
      const input = form && form.querySelector('input[name="id"]');
      return (input && input.value) || '';
    }

    handleVariantChange(event) {
      const data = (event && event.data) || {};
      const info = this.closest('product-info');
      if (data.sectionId && info) {
        const sectionId = info.dataset.originalSection || info.dataset.section;
        if (sectionId && sectionId !== data.sectionId) return;
      }

      const incoming = incomingFromHtml(data.html);
      const variantId = (data.variant && data.variant.id) || this.currentVariantId();

      if (this.dataset.inserted === 'true') {
        const form = this.querySelector('form');
        const live = this.querySelector('shopify-payment-terms');
        if (incoming.form && form) {
          const nextInput = incoming.form.querySelector('input[name="id"]');
          const liveInput = form.querySelector('input[name="id"]');
          if (nextInput && liveInput) liveInput.value = nextInput.value;
        }
        if (incoming.terms && live) {
          const nextId = incoming.terms.getAttribute('variant-id') || variantId;
          if (nextId) live.setAttribute('variant-id', String(nextId));
          const meta = incoming.terms.getAttribute('shopify-meta');
          if (meta) live.setAttribute('shopify-meta', meta);
        } else if (variantId) {
          syncVariant(this, variantId);
        }
        this.dedupe();
        const liveInput = this.querySelector('form input[name="id"]');
        if (liveInput) liveInput.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      const template = this.querySelector('template');
      if (!template) return;
      if (incoming.form) {
        template.content.replaceChildren();
        template.content.appendChild(incoming.form.cloneNode(true));
      } else if (variantId) {
        syncVariant(template.content, variantId);
      }
    }

    dedupe() {
      const extras = liveTerms(this);
      for (let i = 1; i < extras.length; i += 1) extras[i].remove();
    }

    reveal() {
      if (this.dataset.inserted === 'true') return;
      this.dataset.inserted = 'true';
      this.teardown();

      if (liveTerms(this).length) {
        this.dedupe();
        return;
      }

      const template = this.querySelector('template');
      if (!template) return;

      const variantId = this.currentVariantId();
      if (variantId) syncVariant(template.content, variantId);

      this.appendChild(template.content);
      template.remove();
      this.dedupe();
    }
  }

  if (!customElements.get('deferred-payment-terms')) {
    customElements.define('deferred-payment-terms', DeferredPaymentTerms);
  }
})();
